import React, { ReactElement } from 'react';

// language
import { injectIntl, IntlShape } from 'react-intl';

// redux
import { useSelector } from 'react-redux';

// @mui material components
import Card from "@mui/material/Card";
import Divider from '@mui/material/Divider';
import Icon from "@mui/material/Icon";

// components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// types
import { ContainerData, } from 'types/typesRedux';

// icons
import { 
  containerIcon,
  containerClosedIcon,
  containerOpenedWarningIcon,
  containerOpenedIcon,
  containerUnknownIcon,
  kayakSquareIcon,
  paddleSquareIcon,
  bagSquareIcon,
  vestSquareIcon,
  helmetSquareIcon,
} from 'assets/icons';

interface Props {
  intl: IntlShape,
}

function ContainerInfo( { intl } :Props ): JSX.Element {

  const container = useSelector( (state: ContainerData) => state.container.container );

  const handleContainerDoorStatus = ( doorStatus: string ) => {
    if ( doorStatus === intl.formatMessage({ id: 'tableContainers.door.close' }) ) {
      return containerClosedIcon;
    } else if ( doorStatus === intl.formatMessage({ id: 'tableContainers.door.open' }) ) {
      return containerOpenedIcon;
    } else if ( doorStatus === intl.formatMessage({ id: 'tableContainers.door.warning' }) ) {
      return containerOpenedWarningIcon;
    } else {
      return containerUnknownIcon;
    }
  };

  const handleDoorTextColor = ( doorStatus: string ) => {
    if ( doorStatus === intl.formatMessage({ id: 'tableContainers.door.close' }) ) {
      return "#309B0A";
    } else if ( doorStatus === intl.formatMessage({ id: 'tableContainers.door.open' }) ) {
      return "#F5A524";
    } else if ( doorStatus === intl.formatMessage({ id: 'tableContainers.door.warning' }) ) {
      return "#DD2626";
    } else {
      return "#807E7B";
    }
  };

  const renderAccessory = (icon: ReactElement, nameAccessories: string, countAccessories: number) => {
    return (
      <MDBox
        sx={{
          display: "flex", flexDirection: "row",
          justifyContent: "space-between", width: "100%",
        }}
      >
        <MDBox
          display="flex" flexDirection="column"
          sx={{
            width: "auto", justifyContent: "center",
          }}
        >
          {icon}
        </MDBox>
        <MDBox
          sx={{
            display: "flex", flexDirection: "column",
          }}
        >
          <MDTypography variant="button"
            sx={{
              fontSize: "0.75rem", fontWeight: "400",
              color: "#7b809a",
            }}
          >
            {nameAccessories}
          </MDTypography>
          <MDTypography
            sx={{
              display: "flex", flexDirection: "row",
              justifyContent: "flex-end",
              fontSize: "1rem", fontWeight: "700",
            }}
          >
            {countAccessories}
          </MDTypography>
        </MDBox>
      </MDBox>
    )
  }
     
  return (
    <>
      <Card
        sx={{
          borderRadius: ({ borders: { borderRadius } }) => borderRadius.lg,
          width:"100%", padding: "0 1rem",
        }}
      >
         <MDBox        
          display="flex"
          flexDirection="column"
          p={2}
          m={0}
          sx={{ listStyle: "none" }}
        >

          {/* Header */}
          <MDBox        
            display="flex"
            flexDirection="row"
            m={0}
            sx={{
              listStyle: "none", paddingBottom:"0.6rem",
              width: "100%", justifyContent: "space-between",
            }}
          >
            <MDBox
              sx={{
                display: "flex", flexDirection: "row",
              }}
            >
              <MDBox
                className="badge"
                sx={{ 
                mt: "-2.0rem", ml: 0,
                borderRadius: ({ borders: { borderRadius } }) => borderRadius.xl, 
                backgroundColor: "#17C063",
                }}
                display="grid"
                justifyContent="center"
                alignItems="center"
                color="white"
                width="4.5rem"
                height="4.5rem"
                shadow="md"
                borderRadius="lg"
                variant="gradient"  
                fontSize= "2.2rem"
                >
                <Icon sx={{marginBottom:"0.8rem"}} >{containerIcon}</Icon>
              </MDBox>

              <MDBox
                display="flex" flexDirection="column"
                paddingLeft="1.5rem"
              >
                <MDTypography 
                  variant="h5" color="#000"
                  fontWeight="medium" fontSize="19px"
                >
                  { container.name ? container.name : '-'}
                </MDTypography>
                <MDBox
                  display="flex"
                  flexDirection="row"
                >
                  <MDTypography 
                    variant="button" fontWeight="400" fontSize="0.75rem"
                    color="text"
                  >
                    {intl.formatMessage({id:'containerInfo.id'})}
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="400" fontSize="0.75rem"
                    color="text">
                    &nbsp;{ container.id ? container.id : '-'}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
            <MDBox
              sx={{
                display: "flex", flexDirection: "row",
                paddingTop: "0.3rem",
              }}
            >
              <MDTypography
                sx={{
                  fontSize: "0.625rem", fontWeight: "400",
                  color: "#807E7B",
                }}
              >
                {intl.formatMessage({id:'containerInfo.lastOnline'})}&nbsp;
              </MDTypography>
              <MDTypography
                sx={{
                  fontSize: "0.625rem", fontWeight: "700",
                  color: "#807E7B",
                }}
              >
                { container.lastSeenAt }
              </MDTypography>
            </MDBox>      
          </MDBox>
          
          {/* first row - status, doors */}
          <MDBox
            display="flex" flexDirection="row"
            justifyContent="space-between"
            sx={{
              width: "100%",
            }}
          >

            <MDBox
              display="flex" flexDirection="column"
              sx={{
                width: "27%",
              }}
            >
              <MDBox
                display="flex" flexDirection="row"
                sx={{
                  width: "auto", justifyContent: "space-between",
                  paddingBottom: "0.4rem",
                }}
              >
                <MDTypography 
                  variant="button" fontWeight="400" fontSize="0.75rem"
                  color="#000"
                >
                  {intl.formatMessage({id:'containerInfo.isEnabled'})}
                </MDTypography>
                <MDTypography variant="button" fontWeight="400" fontSize="0.75rem"
                  sx={{ 
                    color: container.isEnabled === "V prevádzke" ? "#309B0A" : "#DD2626",
                  }}
                >
                  &nbsp;{ container.isEnabled ? container.isEnabled : '-'}
                </MDTypography>
              </MDBox>
              <MDBox
                display="flex" flexDirection="row"
                sx={{
                  justifyContent: "space-between",
                }}
              >
                <MDTypography 
                  variant="button" fontWeight="400" fontSize="0.75rem"
                  color="#000"
                >
                  {intl.formatMessage({id:'containerInfo.status'})}
                </MDTypography>
                <MDTypography variant="button" fontWeight="400" fontSize="0.75rem"
                  sx={{ color: container.status === "Online" ? "#309B0A" : "#F5A524" }}
                >
                  &nbsp;{ container.status ? container.status : '-'}
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* second column */}
            <MDBox
              display="flex" flexDirection="row"
              sx={{
                width: "27%",
                justifyContent: "space-between",
              }}
            >
              <MDBox
                display="flex" flexDirection="column"
                sx={{
                  width: "auto", justifyContent: "center",
                }}
              >
                {handleContainerDoorStatus(container.doorStatusA)}
              </MDBox>
              <MDBox
                display="flex" flexDirection="column"
                sx={{
                }}
              >
                <MDTypography 
                  variant="button" fontWeight="400" fontSize="0.75rem"
                  sx={{
                    display: "flex", flexDirection: "row",
                    justifyContent: "flex-end", paddingBottom: "0.4rem",
                  }}
                  color="#000"
                >
                  {intl.formatMessage({id:'containerInfo.doorA'})}
                </MDTypography>
                <MDTypography fontWeight="400" fontSize="0.75rem"
                  sx={{
                    display: "flex", flexDirection: "row",
                    justifyContent: "flex-end",
                    color: handleDoorTextColor(container.doorStatusA),
                  }}
                >
                  &nbsp;{ container.doorStatusA ? container.doorStatusA : '-'}
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* third column */}
            <MDBox
              display="flex" flexDirection="row"
              sx={{
                width: "27%",
                justifyContent: "space-between",
              }}
            >
              <MDBox
                display="flex" flexDirection="column"
                sx={{
                  width: "auto", justifyContent: "center",
                }}
              >
                {handleContainerDoorStatus(container.doorStatusB)}
              </MDBox>
              <MDBox
                display="flex" flexDirection="column"
                sx={{
                }}
              >
                <MDTypography 
                  variant="button" fontWeight="400" fontSize="0.75rem"
                  sx={{
                    display: "flex", flexDirection: "row",
                    justifyContent: "flex-end", paddingBottom: "0.4rem",
                  }}
                  color="#000"
                >
                  {intl.formatMessage({id:'containerInfo.doorB'})}
                </MDTypography>
                <MDTypography fontWeight="400" fontSize="0.75rem"
                  sx={{
                    display: "flex", flexDirection: "row",
                    justifyContent: "flex-end", textAlign: "end",
                    color: handleDoorTextColor(container.doorStatusB),
                  }}
                >
                  &nbsp;{ container.doorStatusB ? container.doorStatusB : '-'}
                </MDTypography>
              </MDBox>
            </MDBox>

          </MDBox>

          <Divider
            orientation="horizontal"
            flexItem
            sx={{ 
              width: '100%', height: '1px', marginTop: "0.8rem", 
              marginBottom: "0.8rem", background: 'black',
            }}
          />
          
          {/* inventory */}
          <MDBox
            sx={{
              display: "flex", flexDirection: "column",
              width: "100%",
            }}
          >
            <MDTypography
              sx={{
                fontSize: "0.75rem", fontWeight: "700",
              }}
            >
              {intl.formatMessage({id:'containerInfo.inventory'})}
            </MDTypography>

            <MDBox
              sx={{
              display: "flex", flexDirection: "row",
              width: "100%", justifyContent: "space-between",
            }}
            >

              {/* first column */}
              <MDBox
                sx={{
                  display: "flex", flexDirection: "column",
                  width: "27%", gap: "0.2rem",
                }}
                >
                {renderAccessory(kayakSquareIcon, intl.formatMessage({id:'containerInfo.boats'}), container.boatsCount)}
                {renderAccessory(paddleSquareIcon, intl.formatMessage({id:'containerInfo.paddle'}), container.paddleCount)}
                {renderAccessory(bagSquareIcon, intl.formatMessage({id:'containerInfo.bag'}), container.bagCount)}
              </MDBox>
              
              {/* second column */}
              <MDBox
                sx={{
                  display: "flex", flexDirection: "column",
                  width: "27%", gap: "0.2rem",
                }}
                >
                {renderAccessory(vestSquareIcon, intl.formatMessage({id:'containerInfo.vest'}), container.vestCount)}

                <Divider
                  orientation="horizontal"
                  flexItem
                  sx={{ 
                    width: '100%', height: '1px', marginTop: "0.2rem", 
                    marginBottom: "0rem", background: 'black',
                  }}
                />

                { container.vestVariants.map(vest => {
                  return (
                    <MDBox
                      sx={{
                        display: "flex", flexDirection: "row",
                        justifyContent: "space-between", width: "100%",
                      }}
                    >
                      <MDTypography
                        sx={{
                          fontSize: "0.75rem", fontWeight: "400", color: "#807E7B"
                        }}
                      >
                        {intl.formatMessage({id:'containerInfo.size'}) + ' ' + vest.name}
                      </MDTypography>
                      <MDTypography
                        sx={{
                          fontSize: "0.75rem", fontWeight: "700",
                        }}
                      >
                        {vest.count}
                      </MDTypography>
                    </MDBox>
                  )
                })}
                
              </MDBox>
              
              {/* third column */}
              <MDBox
                sx={{
                  display: "flex", flexDirection: "column",
                  width: "27%", gap: "0.2rem",
                }}
                >
                {renderAccessory(helmetSquareIcon, intl.formatMessage({id:'containerInfo.helmet'}), container.helmetCount)}
                
                <Divider
                  orientation="horizontal"
                  flexItem
                  sx={{ 
                    width: '100%', height: '1px', marginTop: "0.2rem", 
                    marginBottom: "0rem", background: 'black',
                  }}
                />

                { container.helmetVariants.map(helmet => {
                  return (
                    <MDBox
                      sx={{
                        display: "flex", flexDirection: "row",
                        justifyContent: "space-between", width: "100%",
                      }}
                    >
                      <MDTypography
                        sx={{
                          fontSize: "0.75rem", fontWeight: "400", color: "#807E7B"
                        }}
                      >
                        {intl.formatMessage({id:'containerInfo.size'}) + ' ' + helmet.name}
                      </MDTypography>
                      <MDTypography
                        sx={{
                          fontSize: "0.75rem", fontWeight: "700",
                        }}
                      >
                        {helmet.count}
                      </MDTypography>
                    </MDBox>
                  )
                })}

              </MDBox>

            </MDBox>

          </MDBox>


        </MDBox>
      </Card>
    </>

  );
}

export default injectIntl(ContainerInfo);
